import { jwtDecode } from "jwt-decode";
import AppConstants from "../configs/AppConstants";
import EntityConstants from "../configs/EntityConstants";
import {
  isOtelAlertTelemetryEnabled,
  isPublicVerificationPortalRoute,
} from "../configs/featureFlags";
import { clearAuthLocalStorage } from "../utils/clearAuthStorage";

const API_BASE_URL = AppConstants.HTTPS_HOST;
const REFRESH_URL = EntityConstants.GET_REFRESH_ACCESS_TOKEN;

const reportOtelError = (message, attributes) => {
  if (!isOtelAlertTelemetryEnabled()) {
    return;
  }
  import("../telemetry")
    .then((mod) => {
      if (typeof mod.logError === "function") {
        mod.logError(message, attributes);
      }
    })
    .catch(() => {});
};

const isAbortError = (error) => error?.name === "AbortError";

const endpointWithoutQuery = (url) => {
  if (url == null || url === "") {
    return url;
  }
  try {
    const parsed = /^https?:\/\//i.test(String(url))
      ? new URL(url)
      : new URL(String(url), "http://localhost");
    return `${parsed.origin}${parsed.pathname}`;
  } catch {
    return String(url).split("?")[0];
  }
};

const formatApiErrorMessage = (body, status) => {
  if (body && typeof body === "object") {
    const message =
      typeof body.message === "string" ? body.message.trim() : "";
    const error = typeof body.error === "string" ? body.error.trim() : "";
    const detail =
      typeof body.detail === "string"
        ? body.detail.trim()
        : Array.isArray(body.detail)
          ? body.detail
              .map((item) => (typeof item === "string" ? item : ""))
              .filter(Boolean)
              .join(", ")
          : "";

    if (message && error && error !== message) {
      return `${error}: ${message}`;
    }
    if (message) return message;
    if (detail) return detail;
    if (error) return error;

    const validationParts = [];
    Object.entries(body).forEach(([key, value]) => {
      if (["message", "detail", "error", "code", "status"].includes(key)) {
        return;
      }
      if (Array.isArray(value)) {
        validationParts.push(
          `${key}: ${value.filter((item) => typeof item === "string").join(", ")}`,
        );
      } else if (typeof value === "string" && value.trim()) {
        validationParts.push(`${key}: ${value.trim()}`);
      }
    });
    if (validationParts.length > 0) {
      return validationParts.join("; ");
    }
  }

  if (typeof body === "string" && body.trim()) {
    return body.trim();
  }

  return `Request failed with status ${status}`;
};

const reportAndThrowHttpError = async (
  url,
  options,
  response,
  errorMessage = "HTTP error",
) => {
  const method = String(options?.method || "GET").toUpperCase();
  const status = response.status;
  const clonedForThrow = response.clone();

  if (status >= 500) {
    const payload = await response
      .clone()
      .json()
      .catch(() => ({}));
    reportOtelError(formatApiErrorMessage(payload, status), {
      event: "api_error",
      "http.status_code": status,
      "http.method": method,
      endpoint: endpointWithoutQuery(url),
    });
  }

  const error = new Error(errorMessage);
  error.response = clonedForThrow;
  error.httpStatus = status;
  error.httpMethod = method;
  error.httpUrl = url;
  throw error;
};

const reportNetworkError = (url, options, networkError) => {
  if (isAbortError(networkError)) {
    return;
  }
  if (networkError && typeof networkError === "object") {
    networkError.otelNetworkLogged = true;
  }
  reportOtelError(networkError?.message || "Failed to fetch", {
    event: "api_network_error",
    "http.method": String(options?.method || "GET").toUpperCase(),
    endpoint: endpointWithoutQuery(url),
    "http.url": url,
  });
};

let isRefreshing = false;
let requestQueue = [];

const clearRequestQueue = () => {
  while (requestQueue.length > 0) {
    const { reject } = requestQueue.shift();
    reject && reject(new Error("Session expired"));
  }
};

// Force logout helper
const forceLogout = (reason = "session_expired") => {
  if (reason === "session_expired" && isPublicVerificationPortalRoute()) {
    // Preserve portal branding / language caches on the public portal.
    clearAuthLocalStorage();
    clearRequestQueue();
    // No page reload on this path — reset so later 401s are not stuck queued.
    isRefreshing = false;
    return;
  }

  if (reason === "session_expired") {
    localStorage.setItem("logout_reason", "session_timeout");
  }

  localStorage.clear();
  // restore logout_reason after clear
  if (reason === "session_expired") {
    localStorage.setItem("logout_reason", "session_timeout");
  }
  localStorage.setItem(
    "auth_event",
    JSON.stringify({ type: "LOGOUT", reason: reason, t: Date.now() })
  );

  clearRequestQueue();
  window.location.replace("/");
};

const isRefreshTokenExpired = () => {
  try {
    const token = localStorage.getItem("refresh_token");
    if (!token) return true;
    const { exp } = jwtDecode(token);
    return Date.now() >= exp * 1000;
  } catch {
    return true;
  }
};

const handleRefreshToken = async () => {
  if (isRefreshTokenExpired()) {
    forceLogout("session_expired");
    return false;
  }
  try {
    const response = await POST(`${REFRESH_URL}`, {
      refresh: localStorage.getItem("refresh_token"),
    });
    const data = await response.json();
    if (response.ok && data.access) {
      localStorage.setItem("auth_token", data.access);
      localStorage.setItem("refresh_token", data.refresh);
      return true;
    }
    forceLogout("session_expired");
    return false;
  } catch {
    forceLogout("session_expired");
    return false;
  }
};

const handleRequest = async (url, options) => {
  let response;
  try {
    response = await fetch(url, options);
  } catch (networkError) {
    reportNetworkError(url, options, networkError);
    throw networkError;
  }

  if (response.status === 401) {
    const currentResp = await response.clone().json();
    if (
      currentResp.code === "token_not_valid" ||
      currentResp.detail === "Token is invalid or expired"
    ) {
      if (response.url === `${API_BASE_URL}${REFRESH_URL}`) {
        forceLogout("session_expired");
        return null;
      }
      if (!isRefreshing) {
        isRefreshing = true;
        const tokenRefreshed = await handleRefreshToken();
        if (tokenRefreshed) {
          requestQueue.forEach(({ url, options, resolve, reject }) => {
            options.headers["authorization"] = `Bearer ${localStorage.getItem(
              "auth_token"
            )}`;
            handleRequest(url, options).then(resolve).catch(reject);
          });
          requestQueue = [];
          isRefreshing = false;

          options.headers["authorization"] = `Bearer ${localStorage.getItem(
            "auth_token"
          )}`;

          return handleRequest(url, options);
        } else {
          isRefreshing = false;
          throw new Error("Token refresh failed. Please log in again.");
        }
      } else {
        return new Promise((resolve, reject) => {
          requestQueue.push({ url, options, resolve, reject });
        });
      }
    }
  }

  if (!response.ok) {
    await reportAndThrowHttpError(url, options, response);
  }

  return response;
};

export const GET = async (url, params = null) => {
  let fullUrl;
  if (params === null) {
    fullUrl = `${API_BASE_URL}${url}`;
  } else {
    fullUrl = `${API_BASE_URL}${url}/?${params}`;
  }
  let options;
  if (localStorage.getItem("auth_token")) {
    options = {
      method: "GET",
      headers: {
        "X-CSRFToken": localStorage.getItem("csrf_token"),
        authorization: `Bearer ${localStorage.getItem("auth_token")}`,
        "Accept-Language": localStorage.getItem("i18nextLng"),
      },
    };
  } else {
    options = {
      method: "GET",
      headers: {
        "X-CSRFToken": localStorage.getItem("csrf_token"),
        "Accept-Language": localStorage.getItem("i18nextLng"),
      },
    };
  }
  return handleRequest(fullUrl, options);
};

export const GET_PUBLIC = async (url) => {
  const fullUrl = `${API_BASE_URL}${url}`;
  const options = { method: "GET" };
  let response;
  try {
    response = await fetch(fullUrl, options);
  } catch (networkError) {
    reportNetworkError(fullUrl, options, networkError);
    throw networkError;
  }
  if (!response.ok) {
    await reportAndThrowHttpError(
      fullUrl,
      options,
      response,
      "Public API request failed",
    );
  }
  return response;
};

export const POST = async (url, body) => {
  const fullUrl = `${API_BASE_URL}${url}`;
  const options = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-CSRFToken": `${localStorage.getItem("csrf_token")}`,
      authorization: `Bearer ${localStorage.getItem("auth_token")}`,
      "Accept-Language": localStorage.getItem("i18nextLng"),
    },
    body: JSON.stringify(body),
  };
  return handleRequest(fullUrl, options);
};

export const PUT = async (url, body) => {
  const fullUrl = `${API_BASE_URL}${url}`;
  const options = {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      "X-CSRFToken": localStorage.getItem("csrf_token"),
      authorization: `Bearer ${localStorage.getItem("auth_token")}`,
      "Accept-Language": localStorage.getItem("i18nextLng"),
    },
    body: JSON.stringify(body),
  };
  return handleRequest(fullUrl, options);
};

export const DEL = async (url) => {
  const fullUrl = `${API_BASE_URL}${url}`;
  const options = {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      "X-CSRFToken": localStorage.getItem("csrf_token"),
      authorization: `Bearer ${localStorage.getItem("auth_token")}`,
      "Accept-Language": localStorage.getItem("i18nextLng"),
    },
  };
  return handleRequest(fullUrl, options);
};

export const POSTFORMDATA = async (url, formData) => {
  const fullUrl = `${API_BASE_URL}${url}`;
  const options = {
    method: "POST",
    headers: {
      "X-CSRFToken": localStorage.getItem("csrf_token"),
      authorization: `Bearer ${localStorage.getItem("auth_token")}`,
      "Accept-Language": localStorage.getItem("i18nextLng"),
    },
    body: formData,
  };
  return handleRequest(fullUrl, options);
};

export const LOGOUT = async (url, body) => {
  const fullUrl = `${API_BASE_URL}${url}`;
  const options = {
    method: "POST",
    headers: {
      "X-CSRFToken": localStorage.getItem("csrf_token"),
      authorization: `Bearer ${localStorage.getItem("refresh_token")}`,
      "Accept-Language": localStorage.getItem("i18nextLng"),
    },
    body: JSON.stringify(body),
  };
  return handleRequest(fullUrl, options);
};

export const GENLINK = (url) => {
  return `${API_BASE_URL}${url}?token=${localStorage.getItem("auth_token")}`;
};
